import { FormDraft, FormField, FormSection, moveFormField, removeFormField } from './AdminFormsPage.model';

/** The editor uses the canonical schema. */
export interface ComposerField extends FormField {
  children?: ComposerField[];
  properties?: ComposerField[];
  items?: ComposerField;
}
export interface ComposerSection extends Omit<FormSection, 'children'> {
  children: ComposerField[];
}
export type ComposerDraft = FormDraft;
export function persistComposerDraft(draft: ComposerDraft): FormDraft {
  return {
    ...draft,
    name: draft.name.trim()
  };
}
export function structuralFields(field: ComposerField): ComposerField[] {
  if (field.fieldType === 'object') return field.properties ?? [];
  if (field.fieldType === 'array') return field.items ? [field.items] : [];
  return [];
}
export function childFields(field: ComposerField): ComposerField[] {
  return [...structuralFields(field), ...(field.children ?? [])];
}
export function composerSchemaDepth(fields: ComposerField[], fieldId: string): number {
  const parent = fields.find(field => structuralFields(field).some(child => child.id === fieldId));
  return parent ? composerSchemaDepth(fields, parent.id) + 1 : 1;
}
export interface ComposerRow {
  field: ComposerField;
  parentId: string | null;
  depth: number;
  structural: boolean;
}
export function composerRows(section: ComposerSection): ComposerRow[] {
  const visit = (fields: ComposerField[], parentId: string | null, depth: number, structural: boolean): ComposerRow[] => fields.flatMap(field => [{
    field,
    parentId,
    depth,
    structural
  }, ...visit(structuralFields(field), field.id, depth + 1, true), ...visit(field.children ?? [], field.id, depth + 1, false)]);
  return visit(section.children, null, 0, false);
}
export function composerFields(sections: ComposerSection[]): ComposerField[] {
  return sections.flatMap(section => composerRows(section).map(row => row.field));
}
export function composerPositions(sections: ComposerSection[]): Map<string, string> {
  const result = new Map<string, string>();
  const visit = (fields: ComposerField[], prefix: string) => fields.forEach((field, index) => {
    const position = `${prefix}.${index + 1}`;
    result.set(field.id, position);
    visit(childFields(field), position);
  });
  sections.forEach((section, index) => visit(section.children, String(index + 1)));
  return result;
}
export function mapComposerFields(fields: ComposerField[], update: (field: ComposerField) => ComposerField): ComposerField[] {
  return fields.map(field => update({
    ...field,
    ...(field.children ? {
      children: mapComposerFields(field.children, update)
    } : {}),
    ...(field.properties ? {
      properties: mapComposerFields(field.properties, update)
    } : {}),
    ...(field.items ? {
      items: mapComposerFields([field.items], update)[0]!
    } : {})
  }));
}
export function moveComposerField(sections: ComposerSection[], fieldId: string, sectionId: string, targetId?: string): ComposerSection[] {
  const row = sections.flatMap(composerRows).find(row => row.field.id === fieldId);
  if (!row?.structural) return moveFormField(sections, fieldId, sectionId, targetId);
  return sections.map(section => ({
    ...section,
    children: mapComposerFields(section.children, parent => {
      if (parent.id !== row.parentId || parent.fieldType !== 'object') return parent;
      const properties = parent.properties ?? [];
      const from = properties.findIndex(child => child.id === fieldId);
      const to = properties.findIndex(child => child.id === targetId);
      if (from < 0 || to < 0) return parent;
      const next = [...properties];
      next.splice(from, 1);
      next.splice(to, 0, row.field);
      return {
        ...parent,
        properties: next
      };
    })
  }));
}
export function removeComposerField(sections: ComposerSection[], fieldId: string): ComposerSection[] {
  const row = sections.flatMap(composerRows).find(row => row.field.id === fieldId);
  if (!row?.structural) return removeFormField(sections, fieldId);
  const parent = composerFields(sections).find(field => field.id === row.parentId);
  if (parent?.fieldType === 'array' || parent?.properties?.length === 1) return sections;
  return sections.map(section => ({
    ...section,
    children: mapComposerFields(section.children, field => ({
      ...field,
      ...(field.properties ? {
        properties: field.properties.filter(child => child.id !== fieldId)
      } : {})
    }))
  }));
}